import { useCallback, useEffect, useState } from "react";
import useMergeState from "../useMergeState";
import apiMethods from "../../../utils/api";
import { useDeepCompareMemoize } from "../useDeepCompareMemoize";

const useQuery = <T = any>(url: string, propsVariables: any, disabled?: boolean) => {
    // disabled is used to prevent the query from running on mount
    const [state, mergeState] = useMergeState({
        data: null,
        error: null,
        isLoading: true,
        variables: {},
    });
    const [currentUrl, setCurrentUrl] = useState(url);

    const memoizedPropsVariables = useDeepCompareMemoize(propsVariables);
    
    const makeRequest = useCallback(
        (newVariables?: any, isResetVariables?: boolean) => {
            const variables = { ...state.variables, ...(newVariables || {}) };
            let apiVariables = {
                ...(memoizedPropsVariables as any),
                ...variables,
            };

            if (isResetVariables) {
                apiVariables = newVariables;
            }

            mergeState({ isLoading: true, variables: apiVariables });
            apiMethods.get(currentUrl, apiVariables).then(
                (data) => {
                    mergeState({
                        data,
                        error: null,
                        isLoading: false,
                    });
                },
                (error) => {
                    mergeState({
                        data: null,
                        error,
                        isLoading: false,
                    });
                }
            );
        },

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [memoizedPropsVariables, state.variables, currentUrl]
    );

    useEffect(() => {
        if (!disabled) {
            makeRequest();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUrl]);

    const setLocalData = useCallback(
        (updatedData: any) => {
            mergeState({ data: updatedData });
        },
        [mergeState]
    );

    const setLocalUrl = (newUrl: string) => {
        setCurrentUrl(newUrl);
    };

    return [{ ...state, setLocalData, setLocalUrl }, makeRequest];
};

export default useQuery;
